import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import userService from "../services/userService";
import LoadingSpinner from "../components/UI/LoadingSpinner";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [subscriptionLevel, setSubscriptionLevel] = useState();
  const [loading, setLoading] = useState(true);

  // ✅ Route ohne useLocation bestimmen
  const path =
    typeof window !== "undefined" && window.location
      ? window.location.pathname
      : "/";
  const isPublic = path === "/" || path.startsWith("/blog");

  const determineSubscriptionLevel = (userData) => {
    if (!userData) return null;
    if (userData.roles?.includes?.("admin")) return 4;
    const plan = userData.subscription?.planName;
    if (plan === "basic") return 1;
    if (plan === "standard") return 2;
    if (plan === "premium") return 3;
    return 0;
  };

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem("authToken");

    if (!token || token === "undefined" || token === "null") {
      setIsAuthenticated(false);
      setUser(null);
      setSubscriptionLevel(undefined);
      setLoading(false);
      return null;
    }

    try {
      const parts = token.split(".");
      if (parts.length !== 3) throw new Error("Invalid token format");

      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      if (payload.exp <= currentTime) {
        localStorage.removeItem("authToken");
        setIsAuthenticated(false);
        setUser(null);
        setSubscriptionLevel(undefined);
        return null;
      } else {
        setIsAuthenticated(true);
        const res = await userService.me();
        setUser(res.user);
        const level = determineSubscriptionLevel(res.user);
        setSubscriptionLevel(level);
        return res.user;
      }
    } catch (error) {
      console.error("Auth-Fehler:", error);
      localStorage.removeItem("authToken");
      setIsAuthenticated(false);
      setUser(null);
      setSubscriptionLevel(undefined);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
    const interval = setInterval(() => fetchUser(), 3600000);
    return () => clearInterval(interval);
  }, [fetchUser]);

  // 👉 Spinner nur auf **nicht**-öffentlichen Routen
  if (
    !isPublic &&
    (loading || (isAuthenticated && subscriptionLevel === undefined))
  ) {
    return <LoadingSpinner />;
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        user,
        setUser,
        subscriptionLevel,
        loading,
        fetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
